import { useQueries, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import type { Controls } from "./controls";
import { getCodes, getRelease, getReleases, qrCodesKey, qrReadMessage, qrReleaseKey, qrReleasesKey, releasePreviewSvgUrl, type QrRelease, type QrReleaseChange, type QrReleaseSummary } from "./qr-api";
import { CopyButton, Q_RELEASE, ReleaseStatusPill, str } from "./qr-controls";
import { printedAddress, targetLabel, whoWhen } from "./qr-format";
import { ChangeRowButton, DiscardReleaseButton, NewReleaseButton, PublishButton, RemoveChangeButton, RenameReleaseButton, RollbackButton } from "./QrReleaseActions";
import { useQrMe } from "./QrWriteActions";

// The Releases view of the QR Codes tab (spec: os-portal specs/qr-codes-module.md V2).
// A release is a named set of destination changes that go live together.

const RELEASE_COLUMNS: ColumnDef<QrReleaseSummary, unknown>[] = [
  { id: "name", header: "Name", accessorFn: (row) => row.name },
  { id: "status", header: "Status", accessorFn: (row) => row.status, cell: ({ row }) => <ReleaseStatusPill status={row.original.status} /> },
  { id: "changes", header: "Changes", accessorFn: (row) => String(row.change_count), meta: { align: "right" as const } },
  { id: "created", header: "Created", accessorFn: (row) => whoWhen(row.created_by, row.created_at) },
  { id: "published", header: "Published", accessorFn: (row) => whoWhen(row.published_by, row.published_at) },
];

export function ReleasesView({ controls, areaName }: { controls: Controls; areaName: string }) {
  const open = Number(str(controls.values[Q_RELEASE]));
  if (Number.isInteger(open) && open > 0) return <ReleaseDetail controls={controls} id={open} areaName={areaName} />;
  return <ReleasesList controls={controls} areaName={areaName} />;
}

function ReleasesList({ controls, areaName }: { controls: Controls; areaName: string }) {
  const releases = useQuery({ queryKey: qrReleasesKey, queryFn: getReleases });
  const openRelease = (id: number) => controls.onChange({ ...controls.values, [Q_RELEASE]: String(id) });

  if (releases.isError) {
    return (
      <Alert>
        <AlertTitle>Releases unavailable</AlertTitle>
        <AlertDescription>{qrReadMessage(releases.error, areaName)}</AlertDescription>
      </Alert>
    );
  }
  const rows = releases.data?.data.releases ?? [];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-muted-foreground">A release changes where several printed codes go, all at the same moment.</p>
        <div className="ml-auto">
          <NewReleaseButton onCreated={openRelease} />
        </div>
      </div>
      <DataTable
        columns={RELEASE_COLUMNS}
        rows={rows}
        pending={releases.isPending}
        page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
        onPageChange={() => {}}
        onRowClick={(row) => openRelease(row.id)}
        emptyMessage="No releases yet."
        caption="QR code releases, newest first"
      />
    </div>
  );
}

function ReleaseDetail({ controls, id, areaName }: { controls: Controls; id: number; areaName: string }) {
  const me = useQrMe();
  const release = useQuery({ queryKey: qrReleaseKey(id), queryFn: () => getRelease(id) });
  const list = useQuery({ queryKey: qrReleasesKey, queryFn: getReleases });
  const codes = useQuery({ queryKey: qrCodesKey(30), queryFn: () => getCodes(30) });
  // A code may sit in only one draft release, so the picker leaves out the codes in the others.
  const otherDrafts = (list.data?.data.releases ?? []).filter((row) => row.status === "draft" && row.id !== id);
  const others = useQueries({
    queries: otherDrafts.map((row) => ({ queryKey: qrReleaseKey(row.id), queryFn: () => getRelease(row.id) })),
  });
  const back = () => controls.onChange({ ...controls.values, [Q_RELEASE]: null });

  if (release.isError && !release.data) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" data-nonmutating className="self-start" onClick={back}>
          ← Releases
        </Button>
        <Alert>
          <AlertTitle>Release unavailable</AlertTitle>
          <AlertDescription>{qrReadMessage(release.error, areaName)}</AlertDescription>
        </Alert>
      </div>
    );
  }
  const data = release.data?.data;
  if (!data) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const taken = new Set(others.flatMap((query) => query.data?.data.changes.map((change) => change.code_id) ?? []));
  const allCodes = codes.data?.data.codes ?? [];
  const showTests = data.status === "draft" && Boolean(me?.can_edit);
  const columns = releaseChangeColumns(data, allCodes, taken, showTests);
  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" data-nonmutating onClick={back}>
            ← Releases
          </Button>
          <h2 className="text-lg font-semibold text-ks-ink">{data.name}</h2>
          <ReleaseStatusPill status={data.status} />
        </div>
        {data.notes && <p className="text-base whitespace-pre-wrap text-ks-ink">{data.notes}</p>}
        <p className="text-sm text-muted-foreground">
          Created {whoWhen(data.created_by, data.created_at)}
          {data.published_at ? ` · Published ${whoWhen(data.published_by, data.published_at)}` : ""}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <RenameReleaseButton release={data} />
          <DiscardReleaseButton release={data} onDone={back} />
          <PublishButton release={data} />
          <RollbackButton release={data} />
        </div>
      </header>

      <section aria-label="Changes" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold text-ks-ink">Changes</h3>
          <ChangeRowButton release={data} codes={allCodes} taken={taken} />
        </div>
        <DataTable
          columns={columns}
          rows={data.changes}
          page={{ pageIndex: 0, pageSize: Math.max(data.changes.length, 1), rowCount: data.changes.length }}
          onPageChange={() => {}}
          emptyMessage="No codes in this release yet."
          caption={`Destination changes in ${data.name}`}
          wrap
        />
        {showTests && (
          <p className="text-sm text-muted-foreground">
            Test links open the draft destination. They are not counted as scans and stop working once the release is published.
          </p>
        )}
      </section>
    </div>
  );
}

function releaseChangeColumns(release: QrRelease, codes: Parameters<typeof ChangeRowButton>[0]["codes"], taken: ReadonlySet<string>, showTests: boolean): ColumnDef<QrReleaseChange, unknown>[] {
  const columns: ColumnDef<QrReleaseChange, unknown>[] = [
    {
      id: "code",
      header: "Code",
      accessorFn: (row) => row.label,
      cell: ({ row }) => (
        <span className="flex flex-col">
          <span className="font-medium">{row.original.label}</span>
          <span className="text-xs text-muted-foreground">{printedAddress(row.original.printed_url)}</span>
        </span>
      ),
    },
    {
      id: "now",
      header: "Goes to now",
      accessorFn: (row) => targetLabel(row.current_target_url),
      cell: ({ row }) => <span title={row.original.current_target_url}>{targetLabel(row.original.current_target_url)}</span>,
    },
    {
      id: "after",
      header: "Goes to after publish",
      accessorFn: (row) => targetLabel(row.target_url),
      cell: ({ row }) => <span title={row.original.target_url}>{targetLabel(row.original.target_url)}</span>,
    },
  ];
  if (showTests) {
    columns.push({
      id: "test",
      header: "Test link",
      accessorFn: (row) => row.preview_url ?? "",
      cell: ({ row }) =>
        row.original.preview_url ? (
          <span className="flex items-center gap-3">
            <CopyButton text={row.original.preview_url} label="Copy test link" />
            <img
              src={releasePreviewSvgUrl(release.id, row.original.code_id)}
              alt={`Scan to test ${row.original.label}`}
              width={72}
              height={72}
              className="rounded-ks-sm border border-border bg-ks-surface"
            />
          </span>
        ) : (
          "–"
        ),
    });
  }
  if (release.status === "draft") {
    columns.push({
      id: "actions",
      header: "",
      accessorFn: () => "",
      cell: ({ row }) => (
        <span className="flex items-center gap-1">
          <ChangeRowButton release={release} codes={codes} taken={taken} existing={row.original} />
          <RemoveChangeButton release={release} change={row.original} />
        </span>
      ),
    });
  }
  return columns;
}
